// Iter85 / Phase 3E — Controlled BPCS Auto-Scan Panel.
//
// First controlled alternative-strategy auto-scan workflow.
// Reads the latest in-memory market snapshot from
// `MarketSnapshotContext` (Phase 3D), lets the operator pick
// which snapshot symbols to include, then on one explicit click
// builds BPCS payloads (puts only) and POSTs them to the existing
// Iter57 BPCS preview endpoint:
//
//   POST /api/forge/toolbox/scanner/bpcs-preview
//
// Results render as Wheel-style cards via StrategyResultCard +
// ScannerSpreadMetrics(module="BPCS"). Operator validates and
// decides — no execution, no journal write, no persistence.
//
// HARD CONSTRAINTS (asserted by Iter85 tests):
//  * Manual-run only. No `useEffect`. No auto-fire on mount.
//  * No outbound HTTP except the BPCS preview endpoint above.
//  * Never calls `/forge/candidates`, `/market-scanner/*`,
//    `/forge/toolbox/market-snapshot/build`,
//    `/forge/toolbox/scanner/bccs-preview`,
//    `/forge/toolbox/scanner/iron-condor-preview`,
//    `/forge/toolbox/scanner/cfm-shares-preview`,
//    `/forge/toolbox/scanner/cfm-leaps-preview`, broker / account /
//    position / order / journal / admin endpoints.
//  * No `localStorage`, `sessionStorage`, `IndexedDB`, cookies.
//  * No ranking / winner / best / preferred / auto-select language
//    outside safety disclaimers.
//  * Snapshot read is via React Context — this panel never fetches
//    a fresh snapshot itself.
//  * Missing put chain or missing WES context surface explicit
//    warnings — never fabricated values.

import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, Play, Eraser, ShieldAlert,
  AlertTriangle, Search, Clock, CheckCircle2,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";
import { useWESContext } from "@/contexts/WESContextProvider";
import {
  summarizeWesContext, applyWesToSubpayload,
} from "@/components/wesContextPayloadHelpers";
import WesProvenanceStrip from "@/components/WesProvenanceStrip";
import StrategyResultCard from "@/components/StrategyResultCard";
import { ScannerSpreadMetrics } from "@/components/ScannerResultNormalization";

// Single allowed endpoint — hardcoded, never derived from input.
const BPCS_ENDPOINT = "/forge/toolbox/scanner/bpcs-preview";

const STALE_SECONDS = 5 * 60;
const DEFAULT_MAX_SYMBOLS = 5;

function ageSeconds(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

// Build a single per-symbol BPCS sub-payload from a snapshot row.
// Returns `{ payload, warnings, blocked }`. The payload is what the
// scanner expects under `payloads_by_symbol[symbol]`.
function buildPerSymbolPayload(row, wesRow, wesMeta) {
  const warnings = [];
  const chain = Array.isArray(row.chain) ? row.chain : [];
  const puts = chain.filter(r => r && r.type === "put");
  const blocked = puts.length === 0;
  if (blocked) {
    warnings.push("Missing put chain — BPCS unavailable.");
  }

  const sub = {
    spot_price: row.spot_price ?? null,
    chain: puts,
    wes: {},
  };
  // Iter92 — wire in-memory WES context if available. When the WES
  // envelope has no row for this symbol (or the row carries an
  // empty `evaluator_wes_payload`), the helper keeps `sub.wes = {}`
  // and appends the existing
  //   "WES context unavailable — BPCS scan may be structurally incomplete."
  // warning copy. Never fabricates.
  const wesWarnings = applyWesToSubpayload(sub, wesRow, wesMeta, "BPCS");
  wesWarnings.forEach(w => warnings.push(w));
  if (row.ivr != null) sub.ivr = row.ivr;

  // Snapshot provenance — read-only, ignored by evaluator.
  sub.snapshot_meta = {
    snapshot_id: row.__snapshot_id || null,
    snapshot_as_of: row.__snapshot_as_of || null,
    source: row.source || null,
    chosen_expiration: row.chosen_expiration || null,
    dte: row.dte ?? null,
    freshness_age_seconds: ageSeconds(row.__snapshot_as_of),
    warnings,
  };

  return { payload: sub, warnings, blocked };
}

export default function BPCSAutoScanPanel() {
  const { snapshot } = useMarketSnapshot();
  const { wesContext } = useWESContext();
  const wesSummary = summarizeWesContext(wesContext);
  const wesMeta = {
    wesAsOf: wesSummary.asOf,
    fingerprint: wesSummary.fingerprint,
    version: wesSummary.version,
    stale: wesSummary.stale,
  };

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({}); // symbol -> bool
  const [maxSymbols, setMaxSymbols] = useState(String(DEFAULT_MAX_SYMBOLS));
  const [minIvr, setMinIvr] = useState("");
  const [staleSecondsCfg] = useState(STALE_SECONDS);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastScannedAt, setLastScannedAt] = useState(null);
  const [perSymbolWarnings, setPerSymbolWarnings] = useState({});

  const rows = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.rows)) return [];
    return snapshot.rows.filter(r => r && r.symbol);
  }, [snapshot]);

  // Default-select all snapshot symbols the first time the panel
  // sees a new snapshot_id. Pure derived state, no useEffect.
  const seedKey = snapshot?.snapshot_id || "";
  const [seededFor, setSeededFor] = useState("");
  if (seedKey && seedKey !== seededFor) {
    const next = {};
    rows.forEach(r => { next[r.symbol] = true; });
    // setState during render is fine when guarded against loops by
    // a key check (React documented pattern).
    setSelected(next);
    setSeededFor(seedKey);
  }

  function toggleSymbol(sym) {
    setSelected(prev => ({ ...prev, [sym]: !prev[sym] }));
  }
  function selectAll() {
    const next = {}; rows.forEach(r => { next[r.symbol] = true; });
    setSelected(next);
  }
  function clearAll() {
    setSelected({});
  }

  const ageS = ageSeconds(snapshot?.as_of);
  const stale = ageS !== null && ageS > staleSecondsCfg;

  function buildEnvelope() {
    const maxN = Number(maxSymbols);
    const cap = Number.isFinite(maxN) && maxN > 0 ? Math.floor(maxN) : DEFAULT_MAX_SYMBOLS;
    const minIvrNum = minIvr.trim() === "" ? null : Number(minIvr);

    const picked = rows
      .filter(r => selected[r.symbol])
      .filter(r => minIvrNum == null
        || !Number.isFinite(minIvrNum)
        || (r.ivr != null && r.ivr >= minIvrNum))
      .slice(0, cap);

    const watchlist = picked.map(r => r.symbol);
    const payloads_by_symbol = {};
    const warningsPerSymbol = {};
    picked.forEach(r => {
      const enriched = {
        ...r,
        __snapshot_id: snapshot.snapshot_id,
        __snapshot_as_of: snapshot.as_of,
      };
      const { payload, warnings } = buildPerSymbolPayload(
        enriched,
        wesSummary.bySymbol[r.symbol],
        wesMeta,
      );
      payloads_by_symbol[r.symbol] = payload;
      warningsPerSymbol[r.symbol] = warnings;
    });

    return {
      envelope: { watchlist, payloads_by_symbol },
      warningsPerSymbol,
      picked,
    };
  }

  async function runScan() {
    setErrorMsg(null);
    if (!snapshot) {
      setErrorMsg("Build a fresh market snapshot first.");
      return;
    }
    const { envelope, warningsPerSymbol, picked } = buildEnvelope();
    if (picked.length === 0) {
      setErrorMsg("Select at least one snapshot symbol.");
      return;
    }
    setPerSymbolWarnings(warningsPerSymbol);
    setLastRequest(envelope);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${BPCS_ENDPOINT}`, envelope);
      setResult(r.data);
      setLastScannedAt(new Date().toISOString());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function clearResults() {
    setResult(null);
    setErrorMsg(null);
    setLastRequest(null);
    setLastScannedAt(null);
    setPerSymbolWarnings({});
  }

  const lastScannedAge = ageSeconds(lastScannedAt);

  return (
    <div
      data-testid="bpcs-auto-scan-panel"
      className="mb-5 rounded-sm border border-[#007AFF]/30 bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid="bpcs-auto-scan-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#22D3FF]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          BPCS Controlled Auto-Scan
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3e · bpcs only · manual-run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ──────────────────────────────────── */}
          <div
            data-testid="bpcs-auto-scan-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="bpcs-auto-scan-banner-bpcs-only">
              BPCS only.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-manual">
              Manual scan only.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-ranking">
              No ranking.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-winner">
              No winner.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-journal">
              No journal write.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-persistence">
              No persistence.
            </span>
            <span className="text-[#FFB800]/50">·</span>
            <span data-testid="bpcs-auto-scan-banner-no-account-fetch">
              No account / position fetch.
            </span>
          </div>

          {/* ── Snapshot status ────────────────────────────────── */}
          {!snapshot && (
            <div
              data-testid="bpcs-auto-scan-no-snapshot"
              className="px-4 py-3 text-[11px] font-mono text-[#FFB800] border-b border-[#26262B] flex items-start gap-2"
            >
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              <span>
                No in-memory snapshot yet. Build a fresh market
                snapshot in the Market Snapshot Builder, then return
                here to run BPCS against it.
              </span>
            </div>
          )}

          {snapshot && (
            <div
              data-testid="bpcs-auto-scan-snapshot-summary"
              className="px-4 py-2 border-b border-[#26262B] bg-[#00FF88]/5 text-[10px] font-mono flex items-center gap-3 flex-wrap text-[#00FF88]"
            >
              <span data-testid="bpcs-auto-scan-snapshot-id" className="text-[#00FF88]/80">
                snapshot {snapshot.snapshot_id?.slice(0, 8)}
              </span>
              <span className="text-[#00FF88]/40">·</span>
              <span data-testid="bpcs-auto-scan-snapshot-age" className="inline-flex items-center gap-1 text-[#00FF88]/80">
                <Clock size={10} />
                {ageS != null ? `${ageS}s ago` : "—"}
              </span>
              {stale && (
                <span
                  data-testid="bpcs-auto-scan-stale-warning"
                  className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em] inline-flex items-center gap-1"
                >
                  <AlertTriangle size={9} />
                  Snapshot stale — refresh before relying on this scan.
                </span>
              )}
              {snapshot.rate_limited && (
                <span
                  data-testid="bpcs-auto-scan-rate-limited"
                  className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
                >
                  RATE_LIMITED
                </span>
              )}
              {snapshot.partial && (
                <span
                  data-testid="bpcs-auto-scan-partial"
                  className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] uppercase tracking-[0.12em]"
                >
                  PARTIAL
                </span>
              )}
            </div>
          )}

          {/* ── WES context provenance strip (Iter92) ───────────── */}
          {snapshot && (
            <WesProvenanceStrip
              moduleId="bpcs"
              wesSummary={wesSummary}
            />
          )}

          {/* ── Symbol picker + settings ──────────────────────── */}
          {snapshot && (
            <div className="px-4 py-3 border-b border-[#26262B] space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Snapshot symbols
                </span>
                <button
                  type="button"
                  data-testid="bpcs-auto-scan-select-all"
                  onClick={selectAll}
                  className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
                >
                  Select all
                </button>
                <button
                  type="button"
                  data-testid="bpcs-auto-scan-clear-all"
                  onClick={clearAll}
                  className="px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47]"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {rows.map(r => {
                  const puts = (r.chain || []).filter(x => x.type === "put").length;
                  const noPuts = puts === 0;
                  return (
                    <label
                      key={r.symbol}
                      data-testid={`bpcs-auto-scan-symbol-${r.symbol}`}
                      className="flex items-center gap-2 px-2 py-1 rounded-sm border border-[#26262B] bg-[#08080A] text-[10px] font-mono cursor-pointer hover:border-[#007AFF]/40"
                    >
                      <input
                        type="checkbox"
                        data-testid={`bpcs-auto-scan-symbol-${r.symbol}-checkbox`}
                        checked={!!selected[r.symbol]}
                        onChange={() => toggleSymbol(r.symbol)}
                      />
                      <span className="text-[#E0E0E0] font-bold">{r.symbol}</span>
                      <span className="text-[#5F5F66]">PUTS {puts}</span>
                      {noPuts && (
                        <span
                          data-testid={`bpcs-auto-scan-symbol-${r.symbol}-no-puts`}
                          className="px-1 py-0 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/5 text-[8px] uppercase tracking-[0.12em] text-[#FF3366]"
                        >
                          BLOCKED
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[10px] font-mono">
                <label className="flex flex-col">
                  <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Max symbols</span>
                  <input
                    type="text"
                    data-testid="bpcs-auto-scan-max-symbols"
                    value={maxSymbols}
                    onChange={e => setMaxSymbols(e.target.value)}
                    className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none"
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Min IVR (optional)</span>
                  <input
                    type="text"
                    data-testid="bpcs-auto-scan-min-ivr"
                    value={minIvr}
                    onChange={e => setMinIvr(e.target.value)}
                    placeholder="e.g. 30"
                    className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none"
                  />
                </label>
                <label className="flex flex-col">
                  <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">Freshness threshold</span>
                  <span
                    data-testid="bpcs-auto-scan-freshness-threshold"
                    className="text-[#E0E0E0]"
                  >
                    {staleSecondsCfg}s · warning only · no auto-refresh
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid="bpcs-auto-scan-run"
                  onClick={runScan}
                  disabled={loading}
                  className="px-3 py-1 rounded-sm border border-[#007AFF]/40 bg-[#007AFF]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#22D3FF] hover:bg-[#007AFF]/20 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Play size={10} />
                  {loading ? "Scanning…" : "Run BPCS Auto-Scan"}
                </button>
                <button
                  type="button"
                  data-testid="bpcs-auto-scan-clear"
                  onClick={clearResults}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Clear results
                </button>
                {lastScannedAt && (
                  <span
                    data-testid="bpcs-auto-scan-last-scanned"
                    className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] inline-flex items-center gap-1"
                  >
                    <Clock size={9} />
                    Last scanned {lastScannedAge != null ? `${lastScannedAge}s` : "—"} ago
                  </span>
                )}
              </div>

              {errorMsg && (
                <div
                  data-testid="bpcs-auto-scan-error"
                  className="flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
                >
                  <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}
            </div>
          )}

          {/* ── Results ──────────────────────────────────────── */}
          {snapshot && !loading && !result && !errorMsg && (
            <div
              data-testid="bpcs-auto-scan-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No BPCS auto-scan run yet. Pick snapshot symbols and
              click Run BPCS Auto-Scan.
            </div>
          )}

          {snapshot && result && !loading && !errorMsg && (
            <BPCSResult
              result={result}
              lastRequest={lastRequest}
              perSymbolWarnings={perSymbolWarnings}
              snapshot={snapshot}
            />
          )}
        </div>
      )}
    </div>
  );
}

function BPCSResult({ result, lastRequest, perSymbolWarnings, snapshot }) {
  const counts = result.counts || {};
  return (
    <div data-testid="bpcs-auto-scan-result" className="px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span
          data-testid="bpcs-auto-scan-result-version"
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {result.scanner_version}
        </span>
        <span
          data-testid="bpcs-auto-scan-result-module-id"
          className="px-2 py-0.5 rounded-sm border border-[#007AFF]/40 bg-[#007AFF]/10 text-[9px] uppercase tracking-[0.14em] text-[#22D3FF]"
        >
          {result.module_id}
        </span>
        <span
          data-testid="bpcs-auto-scan-result-total"
          className="text-[#E0E0E0]"
        >
          {result.total_symbols} symbols scanned
        </span>
        <span className="ml-auto inline-flex items-center gap-3">
          {["enter", "watch", "blocked", "no_trade", "error"].map(k => (
            <span
              key={k}
              data-testid={`bpcs-auto-scan-result-count-${k}`}
              className="flex items-center gap-1 text-[#8A8A93]"
            >
              <span className="uppercase tracking-[0.12em]">{k}</span>
              <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
            </span>
          ))}
        </span>
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div
          data-testid="bpcs-auto-scan-result-warnings"
          className="mb-3 text-[10px] font-mono text-[#FFB800]"
        >
          {result.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
              {w}
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => {
          const sym = rec.symbol;
          const localWarnings = perSymbolWarnings[sym] || [];
          const merged = {
            ...rec,
            warnings: [
              ...(Array.isArray(rec.warnings) ? rec.warnings : []),
              ...localWarnings,
            ],
          };
          const rowSnap = (snapshot?.rows || []).find(r => r && r.symbol === sym);
          const rowAge = ageSeconds(snapshot?.as_of);
          return (
            <StrategyResultCard
              key={sym}
              rec={merged}
              moduleLabel="BPCS"
              accent={{
                border: "border-[#007AFF]/30",
                glow:   "bg-[#007AFF]/5",
                text:   "text-[#22D3FF]",
              }}
              testidPrefix="bpcs-auto-scan-result-row"
            >
              <div
                data-testid={`bpcs-auto-scan-result-row-snapshot-${sym}`}
                className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]"
              >
                snapshot {snapshot?.snapshot_id?.slice(0, 8) || "—"} ·
                {" "}{rowAge != null ? `${rowAge}s ago` : "—"} ·
                {" "}SRC {rowSnap?.source || "—"} ·
                {" "}EXP {rowSnap?.chosen_expiration || "—"}
              </div>
              <ScannerSpreadMetrics
                rec={merged}
                module="BPCS"
                testidPrefix="bpcs-auto-scan-result-row"
              />
            </StrategyResultCard>
          );
        })}
      </ul>

      <details
        data-testid="bpcs-auto-scan-result-request-json"
        className="mt-3 border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used (read-only)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-64 overflow-auto">
          {JSON.stringify(lastRequest, null, 2)}
        </pre>
      </details>
    </div>
  );
}
